import AppInput from './form/base/app-input';
import { ReactComponent as SearchIcon } from '../assets/icons/search.svg';
import AppSelect from './form/base/app-select';
import AppCheckboxInput from './form/base/app-checkbox-input';
import './table-filters.scss';
import { useEffect, useState, useRef } from 'react';
import { apiService } from '../services/api.service';
import { LabelValue } from '../types/label-value.types';
import { StatusType } from '../types/statistics.types';
import { useDispatch, useSelector } from 'react-redux';
import { AppStore } from '../store/store';
import { UserState } from '../store/user.slice';
import { MerchantState, merchantSliceActions } from '../store/merchant.slice';
import { Roles } from '../utils/roles';

interface TableFiltersProps {
    onFiltersChange: (filters: {
        traceId?: string;
        merchantId?: string;
        midId?: string;
        processorId?: string;
        customerCardType?: string;
        status?: string;
        searchByLogs?: boolean;
    }) => void;
    resetTrigger: boolean;
    showStatus?: boolean;
    showSearchByLogs?: boolean;
}

const TableFilters: React.FC<TableFiltersProps> = ({ onFiltersChange, resetTrigger, showStatus = true, showSearchByLogs = false }) => {
    const [merchantOptions, setMerchantOptions] = useState<LabelValue[]>([]);
    const [processorsOptions, setProcessorsOptions] = useState<LabelValue[]>([]);
    const [cardBrandsOptions, setCardBrandsOptions] = useState<LabelValue[]>([]);
    const [midOptions, setMidOptions] = useState<LabelValue[]>([]);
    const [selectedMerchant, setSelectedMerchant] = useState<string | null>(null);
    const [selectedCard, setSelectedCard] = useState<string | null>(null);
    const [selectedProcessor, setSelectedProcessor] = useState<string | null>(null);
    const [selectedMID, setSelectedMID] = useState<string | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
    const [searchValue, setSearchValue] = useState<string>('');
    const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>('');
    const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [searchByLogs, setSearchByLogs] = useState<boolean>(false);
    const { user } = useSelector<AppStore, UserState>((state) => state.user)
    const { allowedMerchants, selectedMerchantId } = useSelector<AppStore, MerchantState>((state) => state.merchant);
    const dispatch = useDispatch();

    const isMerchant = (user?.roles?.includes(Roles.MERCHANT) || user?.roles?.includes(Roles.BAS_MERCHANT_VIEW)) ?? false;
    const isAdmin = user?.roles?.includes(Roles.ADMIN) ?? false;
    const isMultiMerchantView = user?.roles?.includes(Roles.MULTI_MERCHANT_VIEW) ?? false;


    const statusOptions = Object.values(StatusType).map((status) => ({
        label: status,
        value: status,
    }));

    useEffect(() => {
        if (user?.id && isMerchant) {
            setSelectedMerchant(user.id);
        }
    }, [user])

    

    useEffect(() => {
        setSearchValue('');
        setDebouncedSearchValue('');
        setSearchByLogs(false);
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
            debounceTimeoutRef.current = null;
        }
        if (!isMerchant && !isMultiMerchantView) {
        setSelectedMerchant(null);
        }
        setSelectedProcessor(null);
        setSelectedCard(null);
        setSelectedStatus(null);
        setSelectedMID(null)
    }, [user,resetTrigger]);

    useEffect(() => {
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchValue(searchValue);
            debounceTimeoutRef.current = null;
        }, 1000);
        return () => {
            if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
        };
    }, [searchValue]);

    useEffect(() => {
        setSelectedMID('');
        if (!isMerchant && selectedMerchant) {
        apiService.getMidsByMerchantAndProcessor({ merchantId: selectedMerchant, processorId: selectedProcessor })
            .then((response) => {
                const transformedMids = response.data.map((mid: any) => ({
                    label: mid.midName || mid.midCode || 'Unknown',
                    value: mid.id,
                }));
                setMidOptions([{
                    label: 'Reset',
                    value: '',
                }, ...transformedMids]);
            })
            .catch((error) => console.error('Error fetching mids:', error));
        }

    }, [selectedMerchant, selectedProcessor])

    useEffect(() => {
        if (isMerchant && user?.id) {
            setSelectedMerchant(user.id);
    
            apiService.getMidsByMerchantAndProcessor({ merchantId: user?.id, processorId: selectedProcessor })
                .then((response) => {
                    const transformedMids = response.data.map((mid: any) => ({
                        label: mid.midName || mid.midCode || 'Unknown',
                        value: mid.id,
                    }));
                    setMidOptions([{
                        label: 'Reset',
                        value: '',
                    }, ...transformedMids]);
                })
                .catch((error) => console.error('Error fetching mids:', error));
        }
    }, [isMerchant, user?.id, selectedProcessor]);

    useEffect(() => {
        setTimeout(() => {
        if (isMultiMerchantView) {
            const transformedMerchants = allowedMerchants
                .filter((m) => !!m.id)
                .map((m) => ({
                    label: (m.lastName || m.firstName || m.email || m.id) as string,
                    value: m.id as string,
                }));
            setMerchantOptions(transformedMerchants);
        } else if (!isMerchant) {
            apiService
                .getAllMerchants()
                .then((response) => {
                    const transformedMerchants = response.data.map((merchant: any) => ({
                        label: merchant.lastName || merchant.lastName || 'Unknown',
                        value: merchant.id,
                    }));
                    setMerchantOptions([{
                        label: 'Reset',
                        value: '',
                    }, ...transformedMerchants]);
                })
                .catch((error) => console.error('Error fetching merchants:', error));
        }

        apiService
            .getAllProcessors()
            .then((response) => {
                const transformedProcessors = response.data.map((processor: any) => ({
                    label: processor.processorName || processor.processorType || 'Unknown',
                    value: processor.id,
                }));
                setProcessorsOptions([{
                    label: 'Reset',
                    value: '',
                }, ...transformedProcessors]);
            })
            .catch((error) => console.error('Error fetching processors:', error));


        apiService
            .getAllCardTypes()
            .then((response) => {
                const transformedCards = response.data.map((card: any) => ({
                    label: card || 'Unknown',
                    value: card,
                }));
                setCardBrandsOptions([{
                    label: 'Reset',
                    value: '',
                }, ...transformedCards]);
            })
            .catch((error) => console.error('Error fetching card types:', error));
        }, 500)
    }, [user, isMultiMerchantView, allowedMerchants])

    // Keep the merchant dropdown synced with the globally-selected merchant for multi-merchant users
    useEffect(() => {
        if (isMultiMerchantView) {
            setSelectedMerchant(selectedMerchantId ?? null);
        }
    }, [isMultiMerchantView, selectedMerchantId])


    useEffect(() => {
        onFiltersChange({
            traceId: debouncedSearchValue || undefined,
            midId: selectedMID || undefined,
            merchantId: selectedMerchant || undefined,
            processorId: selectedProcessor || undefined,
            customerCardType: selectedCard || undefined,
            status: selectedStatus || undefined,
            searchByLogs: searchByLogs ? true : undefined,
        });
    }, [debouncedSearchValue, selectedMID, selectedMerchant, selectedCard, selectedProcessor, selectedStatus, searchByLogs]);

    return (
        <div className='table-filters flex-row flex-wrap flex-gap-15'>
            <AppInput
                theme='filter'
                icon={<SearchIcon />}
                value={searchValue}
                placeholder='Search'
                onChange={(event) => {
                    setSearchValue(event.target.value)
                }}
            />
            {!isMerchant && !isMultiMerchantView &&
                <AppSelect
                    theme='filter'
                    value={selectedMerchant}
                    options={merchantOptions}
                    placeholder='Select Merchant'
                    onChange={(value) => setSelectedMerchant(value)}
                />
            }
            <AppSelect
                theme='filter'
                value={selectedCard}
                options={cardBrandsOptions}
                placeholder='Select Card Brand'
                onChange={(value) => setSelectedCard(value)}
            />
            <AppSelect
                theme='filter'
                value={selectedProcessor}
                options={processorsOptions}
                placeholder='Select Channel'
                onChange={(value) => setSelectedProcessor(value)}
            />
            <AppSelect
                theme='filter'
                value={selectedMID}
                options={midOptions}
                placeholder='Select MID'
                onChange={(value) => setSelectedMID(value)}
            />
            {showStatus &&
                <AppSelect
                    theme='filter'
                    value={selectedStatus}
                    options={[{
                        label: 'Reset',
                        value: '',
                    }, ...statusOptions]}
                    placeholder='Select Status'
                    onChange={(value) => setSelectedStatus(value)}
                />
            }
            {showSearchByLogs && isAdmin &&
                <AppCheckboxInput
                    id="search-by-logs"
                    label="Search by logs"
                    checked={searchByLogs}
                    onChange={(e) => setSearchByLogs(e.target.checked)}
                />
            }
        </div>
    )
}

export default TableFilters;